import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  useApiKeys,
  useDeleteApiKey,
  useActivateApiKey,
  useDeactivateApiKey,
} from '../hooks';
import { ApiKeyCard } from '../components/ApiKeyCard';
import { ApiKeyListItem } from '../components/ApiKeyListItem';
import { CreateApiKeyModal } from '../components/CreateApiKeyModal';
import { Button, FiltersButton, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, PlainHeadCell, SelectAllHeadCell, SortableHeadCell, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useApp } from '@/contexts/AppContext';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import type { ApiKeyQuery, ExternalApiKey } from '../types';

type SortKey = 'name' | 'status' | 'usage' | 'lastUsed' | 'expires';

export const ApiKeysPage: React.FC = () => {
  const { app } = useApp();

  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [query, setQuery] = useState<ApiKeyQuery>({ page: 1, limit: 12 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const confirm = useConfirm();

  const { data, isLoading, error, refetch } = useApiKeys(query);
  const deleteApiKey = useDeleteApiKey();
  const activateApiKey = useActivateApiKey();
  const deactivateApiKey = useDeactivateApiKey();

  const items = useMemo(() => data?.items ?? [], [data]);

  // Sort client-side na página carregada (backend não expõe sort para API keys).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'status') cmp = (a.status === 'active' ? 0 : 1) - (b.status === 'active' ? 0 : 1);
      else if (sort.key === 'usage') cmp = (a.usageCount ?? 0) - (b.usageCount ?? 0);
      else if (sort.key === 'lastUsed') cmp = new Date(a.lastUsedAt ?? 0).getTime() - new Date(b.lastUsedAt ?? 0).getTime();
      else cmp = new Date(a.expiresAt ?? 0).getTime() - new Date(b.expiresAt ?? 0).getTime();
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((k) => selectedIds.has(k.id));
  const someOnPageSelected = items.some((k) => selectedIds.has(k.id));

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const setStatusFilter = (status?: 'active' | 'inactive') => {
    setQuery((prev) => ({ ...prev, status, page: 1 }));
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, status: undefined, page: 1 }));
    setFiltersOpen(false);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((k) => next.delete(k.id));
      else items.forEach((k) => next.add(k.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const handleCreate = () => setIsCreateModalOpen(true);

  const handleDelete = async (id: string) => {
    if (await confirm({ message: 'Tem certeza que deseja deletar esta API Key? Esta ação não pode ser desfeita.', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' })) {
      deleteApiKey.mutate(id, {
        onSuccess: () => toast.success('API Key deletada com sucesso'),
        onError: (err: any) => toast.error(err.message || 'Erro ao deletar API Key'),
      });
    }
  };

  const handleToggleStatus = (id: string, currentStatus: string) => {
    if (currentStatus === 'active') {
      deactivateApiKey.mutate(id, {
        onSuccess: () => toast.success('API Key desativada com sucesso'),
        onError: (err: any) => toast.error(err.message || 'Erro ao desativar API Key'),
      });
    } else {
      activateApiKey.mutate(id, {
        onSuccess: () => toast.success('API Key ativada com sucesso'),
        onError: (err: any) => toast.error(err.message || 'Erro ao ativar API Key'),
      });
    }
  };

  const handleOpenDocs = () => {
    if (!app?.apiUrl) return;
    const baseUrl = app.apiUrl.startsWith('http') ? app.apiUrl : `https://${app.apiUrl}`;
    const docsUrl = `${baseUrl.replace(/\/$/, '')}/docs`;
    window.open(docsUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCreateSuccess = () => {
    toast.success('API Key criada com sucesso!');
    refetch();
  };

  const bulkDeactivate = () => {
    const targets = items.filter((k) => selectedIds.has(k.id) && k.status === 'active');
    targets.forEach((k) => deactivateApiKey.mutate(k.id));
    clearSelection();
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selectedCount} API Key(s) selecionada(s)? Esta ação não pode ser desfeita.`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selectedIds).forEach((id) => deleteApiKey.mutate(id));
    clearSelection();
  };

  const statusLabel = query.status === 'active' ? 'Ativas' : query.status === 'inactive' ? 'Inativas' : null;
  const activeFilterCount = [statusLabel].filter(Boolean).length;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={<>
          {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }}>
              Integração
            </span>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Chaves de API</span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
        </>}
        center={
          /* Centro: busca (F17-22: ToolbarSearch shared, sempre centralizada) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar chave…"
            data-testid="apikeys-search"
          />
        }
        right={<>
          {/* Direita — ordem canônica (F15): secundários → Filtros → Toggle → sep → CTA */}
          {/* Documentação da API (Button shared secundário) */}
          {app?.apiUrl && (
            <Button
              variant="secondary"
              icon="external-link"
              collapsibleLabel
              className="flex-none"
              onClick={handleOpenDocs}
              title="Documentação da API"
              data-testid="apikeys-docs-button"
            >
              Documentação
            </Button>
          )}

          {/* Refresh (F07: casca 36×36 bordada + ícone 16 + hover ghost) */}
          <button
            onClick={() => refetch()}
            className="flex items-center justify-center h-9 w-9 rounded-lg transition-colors flex-none"
            style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'; }}
            title="Atualizar"
            disabled={isLoading}
            data-testid="apikeys-refresh"
          >
            <Icon name="refresh" className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          {/* Filtros (F05: FiltersButton shared — mantém popover/estado existente) */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="apikeys-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                >
                  <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
                  {([[undefined, 'Todas'], ['active', 'Ativas'], ['inactive', 'Inativas']] as const).map(([val, label]) => {
                    const active = query.status === val;
                    return (
                      <button
                        key={label}
                        onClick={() => setStatusFilter(val)}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                        style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        data-testid={`apikeys-filter-status-${label}`}
                      >
                        {label}
                        {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: ViewToggle shared — Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="apikeys-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Nova API Key (único primário roxo — Button shared, F01-03) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            className="flex-none"
            onClick={handleCreate}
            data-testid="apikeys-create-button"
          >
            Gerar chave
          </Button>
        </>}
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }}>Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionada(s)</span>
          <span className="flex-1" />
          <button onClick={bulkDeactivate} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }} data-testid="apikeys-bulk-deactivate">
            <Icon name="power" className="w-3.5 h-3.5" /> Desativar
          </button>
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="apikeys-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }}>Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar API Keys: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhuma API Key cadastrada"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Crie sua primeira API Key para começar a integrar aplicações externas'}
              actionLabel="Criar primeira API Key"
              onAction={handleCreate}
            />
          )}

          {/* Grid (Cards) — F34: cards SEM checkbox, seleção em massa só na tabela */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sortedItems.map((apiKey: ExternalApiKey) => (
                <ApiKeyCard
                  key={apiKey.id}
                  apiKey={apiKey}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((apiKey: ExternalApiKey) => (
                  <ApiKeyCard
                    key={apiKey.id}
                    apiKey={apiKey}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        onToggle={toggleSelectAllOnPage}
                        ariaLabel="Selecionar todas"
                        data-testid="apikeys-select-all"
                      />
                      <SortableHeadCell<SortKey> label="Nome" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell label="Chave" />
                      <SortableHeadCell<SortKey> label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell<SortKey> label="Uso" sortKey="usage" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell<SortKey> label="Último uso" sortKey="lastUsed" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell<SortKey> label="Expira em" sortKey="expires" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((apiKey: ExternalApiKey) => (
                      <ApiKeyListItem
                        key={apiKey.id}
                        apiKey={apiKey}
                        selected={selectedIds.has(apiKey.id)}
                        onToggleSelect={toggleSelect}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação (F48: sempre com totalItems) */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              totalItems={data.total}
              pageSize={data.limit}
              itemLabel="chaves"
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
            />
          )}
        </div>
      </div>

      {/* Modal de criação */}
      <CreateApiKeyModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};
